/**
 * The clinic address, saved ONCE PER SITE (owner decisions 8 and 19 of
 * docs/plans/crm-agent-scheduler-mode-plan.md; WP-10). The texting agent's
 * scheduling assistant answers "where are you located?" with the address of
 * the study's site; a study can override it in Settings, Texting agent. The
 * server validates every address before it stores it (one line, 5 to 150
 * plain characters, no web address, phone number or dollar amount), because
 * it is texted to leads word for word.
 *
 * Self-fetching (GET/PUT /api/crm/settings/clinic-sites); read is open to
 * every CRM user, the save is admin-only.
 *
 * `supported` is the Booking hours read's `scheduler_supported`: a deploy rolls
 * this page before the api, and the previous api has no clinic-sites route.
 * Until then (or on a 404 anyway) the card fetches nothing and shows no field,
 * only a note (checkpoint regression L2).
 */
import { useCallback, useEffect, useState } from 'react';
import { Loader2, MapPin } from 'lucide-react';
import api from '../../../services/api';
import { fieldCls } from '../filters';
import { primaryBtnCls } from '../../../ui/styles';
import { Alert } from '../ui';
import { BOOKING_EXTRAS_PENDING } from './schedulerLogic';

interface ClinicSite { site_id: string; name: string | null; address: string | null }

const errMsg = (e: any): string =>
  e?.response?.data?.error || e?.response?.data?.detail || e?.message || 'Request failed';

export default function ClinicAddressesCard({ supported }: { supported: boolean }) {
  const [sites, setSites] = useState<ClinicSite[]>([]);
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [footer, setFooter] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<{ message: string; siteId?: string } | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  // The route answered 404: this api predates it (a deploy mid-roll).
  const [missing, setMissing] = useState(false);

  const apply = (data: any) => {
    const list: ClinicSite[] = data?.sites || [];
    setSites(list);
    setDrafts(Object.fromEntries(list.map((s) => [s.site_id, s.address || ''])));
    setFooter(data?.footer_address || null);
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/crm/settings/clinic-sites');
      apply(res.data);
      setMissing(false);
      setError(null);
    } catch (e: any) {
      if (e?.response?.status === 404) setMissing(true);
      else setError({ message: errMsg(e) });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (supported) load(); }, [supported, load]);

  const changed = sites.filter((s) => (drafts[s.site_id] ?? '').trim() !== (s.address || ''));

  const save = async () => {
    if (!changed.length || saving) return;
    setSaving(true);
    setError(null);
    setNotice(null);
    try {
      const addresses = Object.fromEntries(changed.map((s) => [s.site_id, (drafts[s.site_id] ?? '').trim() || null]));
      const res = await api.put('/api/crm/settings/clinic-sites', { addresses });
      apply(res.data);
      setNotice('Clinic addresses saved.');
    } catch (e: any) {
      if (e?.response?.status === 404) { setMissing(true); return; }
      setError({
        message: e?.response?.status === 403 ? 'Only a CRM admin can change clinic addresses.' : errMsg(e),
        siteId: e?.response?.data?.site_id,
      });
    } finally {
      setSaving(false);
    }
  };

  if (!supported || missing) {
    return (
      <section className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-800">
        <h2 className="flex items-center gap-1.5 text-sm font-semibold text-gray-900 dark:text-gray-100">
          <MapPin className="h-4 w-4 text-primary-600 dark:text-primary-400" /> Clinic address per site
        </h2>
        <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">{BOOKING_EXTRAS_PENDING}</p>
      </section>
    );
  }

  return (
    <section className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-800">
      <h2 className="flex items-center gap-1.5 text-sm font-semibold text-gray-900 dark:text-gray-100">
        <MapPin className="h-4 w-4 text-primary-600 dark:text-primary-400" /> Clinic address per site
      </h2>
      <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
        What the texting agent&apos;s scheduling assistant texts when a lead asks where the clinic is, for studies run at that
        site (through the study&apos;s registry protocol). Leave it empty and that question gets the fixed deflection.
        {footer ? <> Our email footer uses <span className="font-medium text-gray-700 dark:text-gray-200">{footer}</span>.</> : null}
      </p>

      {error && <Alert tone="error" size="sm" icon={false} className="mt-2.5">{error.message}</Alert>}
      {notice && <Alert tone="info" size="sm" icon={false} className="mt-2.5">{notice}</Alert>}

      {loading ? (
        <div className="flex items-center justify-center py-6"><Loader2 className="h-5 w-5 animate-spin text-primary-600" /></div>
      ) : sites.length === 0 ? (
        <p className="mt-2.5 text-sm text-gray-500 dark:text-gray-400">This network has no sites yet.</p>
      ) : (
        <div className="mt-2.5 space-y-2">
          {sites.map((s) => (
            <div key={s.site_id} className="grid grid-cols-1 items-center gap-1 sm:grid-cols-[minmax(0,12rem)_1fr] sm:gap-3">
              <label htmlFor={`clinic-${s.site_id}`} className="truncate text-sm font-medium text-gray-700 dark:text-gray-200" title={s.name || undefined}>
                {s.name || 'Unnamed site'}
              </label>
              <input
                id={`clinic-${s.site_id}`}
                value={drafts[s.site_id] ?? ''}
                maxLength={150}
                onChange={(e) => { setDrafts((d) => ({ ...d, [s.site_id]: e.target.value })); setNotice(null); }}
                placeholder="4218-C Arendell Street, Morehead City, NC 28557"
                aria-invalid={error?.siteId === s.site_id || undefined}
                className={`${fieldCls} w-full ${error?.siteId === s.site_id ? 'border-red-400 dark:border-red-600' : ''}`}
              />
            </div>
          ))}
          <div className="flex items-center gap-3 pt-1">
            <button type="button" onClick={save} disabled={saving || !changed.length} className={primaryBtnCls}>
              {saving && <Loader2 className="h-4 w-4 animate-spin" />}
              {saving ? 'Saving…' : 'Save clinic addresses'}
            </button>
            {changed.length > 0 && !saving && (
              <span className="text-xs text-gray-500 dark:text-gray-400">Unsaved changes</span>
            )}
          </div>
        </div>
      )}
    </section>
  );
}
